import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { countTickets, getUserSettings, listTickets, queryKeys } from '@/api';
import {
  DEFAULT_SETTLED_SUB_FILTERS,
  MY_BETS_DATE_PRESETS,
  TICKETS_PAGE_SIZE,
  myBetsDateFrom,
  ticketFiltersForStatusTab,
  type MyBetsDatePresetDays,
  type MyBetsStatusTab,
  type SettledTicketSubFilters,
  type TicketListItem,
} from '@/domain/tickets';

const DEFAULT_DATE_DAYS: MyBetsDatePresetDays = 7;

export type SettledFilterKey = keyof SettledTicketSubFilters;

export interface UseMyBetsResult {
  statusTab: MyBetsStatusTab;
  setStatusTab: (tab: MyBetsStatusTab) => void;
  dateDays: MyBetsDatePresetDays;
  setDateDays: (days: MyBetsDatePresetDays) => void;
  datePresets: typeof MY_BETS_DATE_PRESETS;
  showDateFilter: boolean;
  settledFilters: SettledTicketSubFilters;
  setSettledFilter: (key: SettledFilterKey, value: boolean) => void;
  tickets: TicketListItem[];
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  listError: Error | null;
  openCount: number;
  liveCount: number;
  settledCount: number | null;
  savedCount: number | null;
  sentinelRef: (node: HTMLDivElement | null) => void;
}

/**
 * My bets filter state + infinite ticket list + open/live counts.
 * Settled/saved badges use the active list's `NumberOfResults` when that tab loads.
 */
export function useMyBets(enabled: boolean): UseMyBetsResult {
  const [statusTab, setStatusTab] = useState<MyBetsStatusTab>('open');
  const [dateDays, setDateDays] = useState<MyBetsDatePresetDays>(DEFAULT_DATE_DAYS);
  const [settledFilters, setSettledFilters] = useState<SettledTicketSubFilters>(
    DEFAULT_SETTLED_SUB_FILTERS,
  );
  const [settledCount, setSettledCount] = useState<number | null>(null);
  const [savedCount, setSavedCount] = useState<number | null>(null);

  const machineGuid = getUserSettings()?.Guid?.trim() ?? '';
  const dateFrom = useMemo(() => myBetsDateFrom(dateDays), [dateDays]);
  const showDateFilter = statusTab !== 'live';

  const setSettledFilter = useCallback((key: SettledFilterKey, value: boolean) => {
    setSettledFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const tableFilters = useMemo(
    () =>
      ticketFiltersForStatusTab(
        statusTab,
        machineGuid,
        showDateFilter ? dateFrom : null,
        settledFilters,
      ),
    [dateFrom, machineGuid, settledFilters, showDateFilter, statusTab],
  );

  const listFilterKey = useMemo(
    () => ({
      tab: statusTab,
      dateFrom: showDateFilter ? dateFrom : null,
      machineGuid,
      settled: statusTab === 'settled' ? settledFilters : null,
    }),
    [dateFrom, machineGuid, settledFilters, showDateFilter, statusTab],
  );

  const countQuery = useQuery({
    queryKey: [...queryKeys.tickets.count(), { dateFrom, machineGuid }],
    queryFn: ({ signal }) =>
      countTickets({ Value: machineGuid, DateFrom: dateFrom }, signal),
    enabled: enabled && Boolean(machineGuid),
  });

  const listQuery = useInfiniteQuery({
    queryKey: queryKeys.tickets.list(listFilterKey),
    enabled: enabled && Boolean(machineGuid),
    initialPageParam: 1,
    queryFn: async ({ pageParam, signal }) => {
      const response = await listTickets(
        {
          TableFilters: tableFilters,
          Paging: {
            PageNumber: pageParam,
            NumberOfResultsToDisplay: TICKETS_PAGE_SIZE,
          },
          Sorting: { SortBy: 'Timestamp', Direction: 'desc' },
        },
        signal,
      );
      return response;
    },
    getNextPageParam: (lastPage) => {
      const page = lastPage.Filter?.PageNumber ?? 1;
      const pages = lastPage.Filter?.NumberOfPages ?? 1;
      return page < pages ? page + 1 : undefined;
    },
  });

  useEffect(() => {
    const first = listQuery.data?.pages[0];
    if (!first?.Filter || listQuery.isFetching) return;
    const total = first.Filter.NumberOfResults ?? first.Data?.length ?? 0;
    if (statusTab === 'settled') setSettledCount(total);
    if (statusTab === 'saved') setSavedCount(total);
  }, [listQuery.data, listQuery.isFetching, statusTab]);

  const tickets = useMemo(
    () => listQuery.data?.pages.flatMap((page) => page.Data ?? []) ?? [],
    [listQuery.data],
  );

  const [sentinelNode, setSentinelNode] = useState<HTMLDivElement | null>(null);
  const fetchNextPage = listQuery.fetchNextPage;
  const hasNextPage = Boolean(listQuery.hasNextPage);
  const isFetchingNextPage = listQuery.isFetchingNextPage;

  const sentinelRef = useCallback((node: HTMLDivElement | null) => {
    setSentinelNode(node);
  }, []);

  useEffect(() => {
    if (!sentinelNode || !enabled || !hasNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting) && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      { root: null, rootMargin: '120px', threshold: 0 },
    );
    observer.observe(sentinelNode);
    return () => observer.disconnect();
  }, [enabled, fetchNextPage, hasNextPage, isFetchingNextPage, sentinelNode]);

  return {
    statusTab,
    setStatusTab,
    dateDays,
    setDateDays,
    datePresets: MY_BETS_DATE_PRESETS,
    showDateFilter,
    settledFilters,
    setSettledFilter,
    tickets,
    isLoading: listQuery.isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage: () => {
      void fetchNextPage();
    },
    listError: listQuery.error instanceof Error ? listQuery.error : null,
    openCount: countQuery.data?.OpenTickets ?? 0,
    liveCount: countQuery.data?.LiveTickets ?? 0,
    settledCount,
    savedCount,
    sentinelRef,
  };
}
